import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, DollarSign, Clock, Repeat } from "lucide-react";

export default function Book() {
  const nav = useNavigate();
  const [services, setServices] = useState([]);
  const [pets, setPets] = useState([]);
  const [service, setService] = useState(null);
  const [petId, setPetId] = useState("");
  const [date, setDate] = useState(new Date(Date.now() + 86400e3));
  const [time, setTime] = useState("10:00");
  const [notes, setNotes] = useState("");
  const [recurring, setRecurring] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data));
    api.get("/pets").then(({ data }) => { setPets(data); if (data[0]) setPetId(data[0].id); });
  }, []);

  const submit = async () => {
    if (!service) { toast.error("Pick a service"); return; }
    const [h, m] = time.split(":");
    const dt = new Date(date); dt.setHours(+h, +m, 0, 0);
    setBusy(true);
    try {
      const res = await api.post("/appointments", {
        service_id: service.id,
        pet_id: petId || null,
        start: dt.toISOString(),
        notes,
        recurring,
        interval_weeks: 4,
        occurrences: 6,
      });
      const count = res.data.series_count || 1;
      toast.success(count > 1
        ? `Booked ${count} appointments (every 4 weeks). Confirmation on the way.`
        : "Appointment booked! Confirmation on the way.");
      nav("/client");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <header>
        <div className="label-accent">Book</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">Schedule a groom</h1>
      </header>

      <section>
        <h2 className="font-display text-lg font-bold mb-3">Pick a service</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {services.map(s => (
            <button key={s.id} onClick={() => setService(s)}
              className={`text-left rounded-2xl border-2 p-4 transition-all ${
                service?.id === s.id
                  ? "border-[#C85A17] bg-[#F5E6CF]"
                  : "border-[#E5DCCE] bg-white hover:border-[#C85A17]/40"
              }`}
              data-testid={`svc-${s.id}`}>
              <div className="flex items-start justify-between">
                <h3 className="font-display font-bold">{s.name}</h3>
                {service?.id === s.id && <CheckCircle2 className="w-5 h-5 text-[#C85A17]"/>}
              </div>
              <p className="text-xs text-[#6B625B] mt-1 min-h-[2.5rem]">{s.description}</p>
              <div className="flex items-center gap-3 mt-3 text-sm">
                <span className="flex items-center gap-1 text-[#C85A17] font-semibold"><DollarSign className="w-4 h-4"/>{s.price}</span>
                <span className="flex items-center gap-1 text-[#6B625B]"><Clock className="w-4 h-4"/>{s.duration_min}m</span>
              </div>
            </button>
          ))}
        </div>
      </section>

      <section className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-3xl border border-[#E5DCCE] p-5 shadow-sm">
          <h2 className="font-display text-lg font-bold mb-3">Pick a date</h2>
          <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)}
            disabled={{ before: new Date() }} data-testid="book-calendar"/>
        </div>
        <div className="bg-white rounded-3xl border border-[#E5DCCE] p-5 shadow-sm space-y-3">
          <h2 className="font-display text-lg font-bold">Details</h2>
          <div>
            <Label>Time</Label>
            <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="book-time"/>
          </div>
          {pets.length > 0 && (
            <div>
              <Label>Which pet?</Label>
              <Select value={petId} onValueChange={setPetId}>
                <SelectTrigger data-testid="book-pet"><SelectValue placeholder="Choose pet"/></SelectTrigger>
                <SelectContent>
                  {pets.map(p => <SelectItem key={p.id} value={p.id}>{p.name} · {p.breed}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Anything the groomer should know?</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Sensitivities, gate code, preferred shampoo…"
              data-testid="book-notes"/>
          </div>
          <label className={`flex items-start gap-3 rounded-2xl border-2 p-3 cursor-pointer transition-colors ${
            recurring ? "border-[#C85A17] bg-[#F5E6CF]" : "border-[#E5DCCE] hover:border-[#C85A17]/40"
          }`}>
            <Checkbox checked={recurring} onCheckedChange={setRecurring}
              className="mt-0.5" data-testid="book-recurring"/>
            <div className="flex-1">
              <div className="font-semibold text-sm flex items-center gap-1.5">
                <Repeat className="w-4 h-4 text-[#C85A17]"/> Repeat every 4 weeks
              </div>
              <div className="text-xs text-[#6B625B] mt-0.5">
                We'll auto-book the next <strong>6 visits</strong> at the same time — you can cancel any of them later.
              </div>
            </div>
          </label>
          <Button onClick={submit} disabled={busy}
            className="w-full rounded-full bg-[#C85A17] hover:bg-[#A9480F] btn-tactile h-12" data-testid="btn-book-submit">
            {busy ? "Booking…" : recurring ? "Confirm 6 appointments" : "Confirm appointment"}
          </Button>
        </div>
      </section>
    </div>
  );
}
